<template>
  <div
    id="prompting-item"
    class="flex items-center w-full gap-4 "
    :class="{ 'py-2 border-b border-grey-200': readOnly }"
  >
    <div
      v-if="format === 'custom' && readOnly"
      class="flex items-center flex-shrink-0 w-15 h-11"
    >
      <BlTextBody1 :text="data.score" />
    </div>
    <input
      v-else-if="format === 'custom'"
      type="number"
      class="flex-shrink-0 px-4 text-sm border rounded outline-none w-15 h-11 border-grey-200 focus:border-light-purple text-grey"
      :class="{ 'border-error-400': errors.score }"
      v-model="data.score"
      :disabled="isDefault"
      @blur.prevent="onUpdate"
    />

    <div v-if="readOnly" class="flex items-center flex-shrink-0 w-15 h-11">
      <BlTextBody1 :text="data.abbreviation" />
    </div>
    <input
      v-else
      type="text"
      class="flex-shrink-0 px-4 text-sm border rounded outline-none w-15 h-11 border-grey-200 focus:border-light-purple text-grey"
      :class="{ 'border-error-400': errors.abbreviation }"
      v-model="data.abbreviation"
      :disabled="isDefault"
      @blur.prevent="onUpdate"
    />

    <div v-if="readOnly" class="flex-shrink-0 w-24">
      <BlIcon
        v-if="data.shape && data.color"
        class="w-6 h-6"
        :icon="`prompt-${data.shape}`"
        :color="data.color"
      />
    </div>
    <div v-else class="relative">
      <div
        :data-id="`prompt-symbol-${data.id}`"
        :class="[
          symbolOpened ? 'border-light-purple' : 'border-grey-200',
          readOnly ? 'pointer-events-none' : '',
          isDefault ? 'pointer-events-none bg-slate-200' : ''
        ]"
        class="relative flex items-center justify-between w-24 px-4 border rounded cursor-pointer h-11"
        @click.prevent="symbolOpened = !symbolOpened"
      >
        <BlIcon
          v-if="data.shape && data.color"
          class="w-6 h-6"
          :icon="'prompt-' + data.shape"
          :color="data.color"
        />
        <BlIcon
          :data-id="`prompt-symbol-${data.id}`"
          class="absolute w-5 h-5 transition-all duration-300 right-4"
          icon="chevron-down"
          :style="symbolOpened ? 'rotate: 180deg' : ''"
        />
      </div>
      <div
        :data-id="`prompt-symbol-${data.id}`"
        v-if="symbolOpened"
        class="absolute left-0 z-10 px-4 py-4 mt-2 space-y-4 overflow-y-auto border rounded shadow-md w-88 max-h-48 bg-pure-white border-grey-200"
      >
        <div :data-id="`prompt-symbol-${data.id}`">
          <BlTextBody2 text="Colors:" />
          <div :data-id="`prompt-symbol-${data.id}`" class="flex gap-2">
            <div
              :data-id="`prompt-symbol-${data.id}`"
              v-for="color in Object.keys(promptColors)"
              :key="color"
              class="flex-shrink-0 w-6 h-6 border-2 rounded cursor-pointer"
              :class="[
                data.color === color
                  ? 'border-light-purple'
                  : 'border-transparent'
              ]"
              :style="{ backgroundColor: promptColors[color].primaryColor }"
              @click="data.color = color"
              :title="color"
            />
          </div>
        </div>
        <div :data-id="`prompt-symbol-${data.id}`">
          <BlTextBody2 text="Shapes:" />
          <div :data-id="`prompt-symbol-${data.id}`" class="flex gap-2">
            <div
              :data-id="`prompt-symbol-${data.id}`"
              v-for="shape in Object.keys(shapes)"
              :key="shape"
              class="flex-shrink-0 w-8 h-8 text-center cursor-pointer"
              @click="data.shape = shape"
              :title="shape"
            >
              <BlIcon
                :data-id="`prompt-symbol-${data.id}`"
                :icon="shapes[shape].icon"
                :color="data.shape === shape ? 'prompt-active' : 'prompt-idle'"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="readOnly" class="flex items-center w-full h-11">
      <BlTextBody1 :text="data.name" />
    </div>
    <input
      v-else
      type="text"
      class="w-full px-4 text-sm border rounded outline-none h-11 border-grey-200 focus:border-light-purple text-grey"
      :class="{ 'border-error-400': errors.name }"
      v-model="data.name"
      :disabled="isDefault"
      @blur.prevent="onUpdate"
    />

    <div
      v-if="!readOnly"
      class="flex items-center cursor-pointer"
      :class="{ 'cursor-not-allowed ': disabledDelete || isDefault }"
      :disabled="disabledDelete || isDefault"
      @click="openDelete"
    >
      <BlIcon
        class="w-5 h-5"
        icon="delete"
        tooltipPosition="left"
        :color="disabledDelete || isDefault ? 'grey' : 'error'"
        :tooltip="tooltipDelete"
      />
    </div>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete the prompt"
      label="Delete"
      description="Deleting this prompt from the repository will only affect new targets going forward. Are you sure you want to proceed? This action is irreversible."
      type="error"
      empty
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "RepositoryPromptingItem",
  components: {
    BlBackdrop,
    BlTextBody1,
    BlTextBody2,
    BlIcon
  },
  props: {
    prompt: { type: Object },
    format: { type: String, default: "classic" }, // classic | custom
    readOnly: { type: Boolean, default: false },
    directDelete: { type: Boolean, default: false },
    disabledDelete: { type: Boolean, default: false },
    tooltipDelete: { type: String, default: "" }
    // @update(data)
    // @delete(data)
  },
  data() {
    return {
      data: {
        id: null,
        score: null,
        name: "",
        abbreviation: "",
        shape: "",
        color: "",
        is_default: false
      },
      errors: {
        name: false,
        abbreviation: false,
        score: false
      },
      symbolOpened: false,
      deleteOpened: false,
      promptColors: {
        cherry: {
          primaryColor: "#FF4447",
          secondaryColor: "#FF4447",
          textColor: "#FFFFFF"
        },
        blush: {
          primaryColor: "#FFC1C2",
          secondaryColor: "#FF4447",
          textColor: "#FF4447"
        },
        gold: {
          primaryColor: "#F4D430",
          secondaryColor: "#F4D430",
          textColor: "#FFFFFF"
        },
        daffodil: {
          primaryColor: "#FBEEAC",
          secondaryColor: "#F4D430",
          textColor: "#9B7600"
        },
        lake: {
          primaryColor: "#2AB1A8",
          secondaryColor: "#2AB1A8",
          textColor: "#FFFFFF"
        },
        mint: {
          primaryColor: "#B8E5E2",
          secondaryColor: "#2AB1A8",
          textColor: "#2AB1A8"
        },
        sky: {
          primaryColor: "#487ADF",
          secondaryColor: "#487ADF",
          textColor: "#FFFFFF"
        },
        hydrangeas: {
          primaryColor: "#D3DFF7",
          secondaryColor: "#487ADF",
          textColor: "#487ADF"
        },
        grey: {
          primaryColor: "#E4E4E4",
          secondaryColor: "#A0A0A0",
          textColor: "#A0A0A0"
        },
        primary: {
          primaryColor: "#9A8AC2",
          secondaryColor: "#9A8AC2",
          textColor: "#FFFFFF"
        }
      },
      shapes: {
        square: { icon: "prompt-square", pointStyle: "rectRounded" },
        circle: { icon: "prompt-circle", pointStyle: "circle" },
        triangle: { icon: "prompt-triangle", pointStyle: "triangle" },
        diamond: { icon: "prompt-diamond", pointStyle: "rectRot" }
      }
    };
  },
  computed: {
    isDefault() {
      if (this.format !== "custom") return false;
      // check for prompting_format === custom
      if (this.data.is_default) return true;
      return false;
    }
  },
  watch: {
    prompt: {
      deep: true,
      handler(val) {
        this.data = val;
      }
    },
    symbolOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      } else {
        this.onUpdate();
      }
    }
  },
  mounted() {
    this.data = { ...this.prompt };
  },
  methods: {
    ...mapActions(["showToast"]),
    onUpdate() {
      const { name, abbreviation, score } = this.data;
      if (!name) {
        this.errors.name = true;
        const title = "The prompt name should not empty.";
        this.showToast({ title, message: "", type: "error" });
        return;
      }
      this.errors.name = false;

      if (!abbreviation.length || abbreviation.length > 3) {
        this.errors.abbreviation = true;
        const title =
          "The prompt code should consist of at least 1 and no more than 3 characters.";
        this.showToast({ title, message: "", type: "error" });
        return;
      }
      this.errors.abbreviation = false;

      if (this.format === "custom") {
        if (score === null || score < 0 || score > 100) {
          this.errors.score = true;
          const title = `Invalid input`;
          const message = `The prompt score must be less than or equal to 100 percent(%).`;
          this.showToast({ title, message, type: "error" });
          return;
        }
        this.errors.score = false;
      }

      this.$emit("update", this.data);
    },
    openDelete() {
      if (this.directDelete) {
        this.onRemove();
      } else {
        this.deleteOpened = true;
      }
    },
    onRemove() {
      this.deleteOpened = false;
      this.$emit("delete", this.data);
    },
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (`prompt-symbol-${this.data.id}` === id) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.symbolOpened = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/repository/prompting/Item.vue